import React from "react";
import { Helmet } from "react-helmet";
import Img from "gatsby-image";
import { graphql, useStaticQuery } from "gatsby";

import Content from "../components/Content";
import { useWelshMap } from "../useWelsh";
import * as classes from "./Pages.module.css";

const map = {
  english: {
    title: "The Proposal",
    intro: `Penpergwm is a proposed new solar farm on lands at Penpergwm, approximately 4km southeast of Abergavenny, Monmouthshire. The installation will have a peak power output of up to 40MW (megawatts), sufficient to produce enough clean, renewable energy to power the equivalent of 11,180 local homes annually.  An estimated 18,000 tonnes of CO2 will be saved each year.`,
    location: {
      title: "Location",
      content: `The site comprises 11 agricultural fields totalling 42 hectares. Not all of the site area will see panels installed. We have already excluded the more visible areas of the site and avoided locations which are close to existing properties. In addition, we have worked with the landowner to ensure large areas are maintained for cattle grazing which, unlike sheep, cannot graze between the panels.
      We selected this site for the following reasons:`,
      bullets: `It is located directly under the existing electricity network, avoiding the need for long disturbing cables and pylons across the countryside;
      The site has good solar irradiation levels;
      It lies outside of any environmental and landscape designations;
      The location offers the opportunity for significant wildlife enhancements which can offer significant biodiversity gain;
      The installation will result in no irreversible impacts;
      The majority of the site is currently used for sheep grazing which can continue alongside the generation of renewable energy. Using a low intensity grazing regime will allow agricultural activities to continue, therefore the site will have a dual use;
      Established hedgerows and boundary trees which provide screening and break up views of the proposed site. These landscape features will be retained and supplemented via new planting to mitigate impacts.
      The site is located in a rural area without close proximity to settlements and few residential properties will experience impactful views from the proposal; and
      The proposed installation would be hosted by landowners looking to diversify to secure the medium term future of this farm for the next generation.`,
    },
    proposal: {
      title: "Our Proposal",
      content: `The solar farm will have a capacity of up to 40MWp and will be operational for up to 40 years.  After this time all of the installation will be removed and the land restored to how it was before, but with improved soil health and biodiversity. The power generated will be exported to the grid via a substation connecting to existing powerlines which run through the site boundary. No new pylons will be necessary to facilitate that connection.
      The solar panels will be installed in rows which run east to west through the site. These rows will be tilted towards the south to capture the maximum amount of solar irradiation possible. The total height of the panels will be no more than 2.8m above the ground with a gap of more than 0.8m above the ground at its minimum.
      Small electric cabins will be located amongst the panels and these will be accessed by a network of crushed stone track which will run through the site. All of the panels will be surrounded by a deer fencing to protect the equipment from large animals entering the site. CCTV cameras will be located periodically around the site perimeter for security.
      The development will also involve additional landscaping including hedgerow planting and improved biodiversity management. The site is currently pasture and arable land with low ecological value, however with the implementation of our proposed Green Infrastructure Plan, the site will be significantly enhanced for biodiversity by creating a range of new habitats, offering food and shelter to wildlife.  This includes a large area of wildflower meadow that will be open for public access with linkages to the existing footpath network.
      The Application Site will be accessed via an improved farm access situated on the southern boundary.  Traffic will approach the site entrance from the south using a local road from Penpergwm for approximately 800m.  Traffic will be routed to Penpergwm from the north via the B4598.  This road connects to the strategic road network south of Abergavenny at the A40 / A465 interchange.`,
    },
  },
  welsh: {
    title: "Y Cynnig",
    intro: `Mae Fferm Solar Penpergwm yn fferm solar newydd arfaethedig ar dir ym Mhenpergwm, tua 4.7km i'r de-orllewin i'r Fenni yn Sir Fynwy. Bydd y safle’n cynhyrchu allbwn pŵer o hyd at 45MW (megawat) ar ei fwyaf, sy'n ddigonol i gynhyrchu digon o ynni glân ac adnewyddadwy i bweru'r hyn sy'n cyfateb i 13,200 o gartrefi lleol bob blwyddyn.`,
    location: {
      title: "Lleoliad",
      content: `Mae'r safle'n cynnwys 11 o gaeau amaethyddol sy'n dod i gyfanswm o 42 hectar. Ni fydd paneli’n cael eu gosod ar holl arwynebedd y safle. Nid ydym wedi cynnwys rhannau o'r safle sy’n fwy gweladwy ac rydym wedi osgoi lleoliadau sy'n agos at eiddo presennol. Hefyd, rydym wedi gweithio gyda'r perchennog tir i sicrhau bod ardaloedd mawr yn cael eu cynnal a'u cadw fel porfa ar gyfer gwartheg oherwydd, yn wahanol i ddefaid, ni allant bori rhwng y paneli.`,
      bullets: `Dewiswyd y safle hwn gennym am y rhesymau canlynol:
      Fe'i lleolir yn union o dan y rhwydwaith trydan presennol, gan osgoi'r angen am geblau a pheilonau hir sy’n gallu amharu ar gefn gwlad;
      Mae gan y safle lefelau arbelydred solar da;
      Mae y tu allan i unrhyw ddynodiadau amgylcheddol a thirwedd;
      Mae'r lleoliad gwledig yn gyfle i wella bywyd gwyllt yn sylweddol a gallai bioamrywiaeth elwa’n sylweddol yn sgil hynny;
      Ni fydd y safle’n arwain at unrhyw effeithiau na ellir eu dadwneud;
      Defnyddir y rhan fwyaf o'r safle ar hyn o bryd fel porfa ar gyfer defaid. Gall yr arfer hwn barhau ochr yn ochr â chynhyrchu ynni adnewyddadwy. Bydd defnyddio system bori dwysedd isel yn caniatáu i weithgareddau amaethyddol barhau, felly bydd dau ddefnydd i'r safle; a
      Cheir gwrychoedd a blannwyd ers amser a choed ar hyd y terfyn sy'n sgrinio ac yn torri ar olygfeydd o'r safle arfaethedig. Bydd y nodweddion hyn o’r dirwedd yn cael eu cadw ac ychwanegir atynt drwy gynllun plannu newydd i liniaru effeithiau.
      Mae'r safle wedi'i leoli mewn ardal wledig heb fod yn agos at aneddiadau ac ychydig o eiddo preswyl fydd yn cael eu heffeithio gan olygfeydd o’r fferm solar;
      Byddai'r safle arfaethedig yn cael ei gynnal gan berchnogion tir cefnogol iawn sy'n ceisio arallgyfeirio i sicrhau dyfodol tymor canolig y fferm hon ar gyfer y genhedlaeth nesaf.`,
    },
    proposal: {
      title: "Ein Cynnig",
      content: `Os caniateir y fferm solar bydd yn gallu cynhyrchu hyd at 45MWp a bydd yn weithredol am hyd at 40 mlynedd. Ar ôl hynny bydd yr holl safle’n cael ei glirio a'r tir yn cael ei adfer fel yr oedd o'r blaen, ond bydd iechyd y pridd a bioamrywiaeth mewn gwell cyflwr. Bydd y pŵer a gynhyrchir yn cael ei allforio i'r grid drwy is-orsaf sy'n cysylltu â llinellau pŵer presennol sy'n mynd ar hyd ffin y safle. Ni fydd angen peilonau newydd i hwyluso'r cysylltiad hwnnw.
      Bydd y paneli solar yn cael eu gosod mewn rhesi sy'n rhedeg o'r dwyrain i'r gorllewin drwy'r safle. Bydd y rhesi hyn yn gwyro tua'r de i ddal cymaint â phosibl o arbelydred solar. Ni fydd uchder y paneli yn fwy na 2.75m uwchben y ddaear gyda bwlch o fwy na 0.8m uwchben y ddaear o leiaf.
      Bydd cabanau trydan bach wedi'u lleoli ymhlith y paneli a bydd modd mynd at y rhain ar hyd rhwydwaith o draciau cerrig wedi'u malu a fydd yn rhedeg drwy'r safle. Bydd ffens ceirw yn amgylchynu pob un o'r paneli i ddiogelu'r offer rhag anifeiliaid mawr sy'n dod i mewn i'r safle. Bydd camerâu teledu cylch cyfyng yn cael eu gosod bob hyn a hyn o amgylch perimedr y safle er mwyn diogelwch.
      Bydd y datblygiad hefyd yn cynnwys gwaith tirweddu ychwanegol gan gynnwys plannu gwrychoedd a rheoli bioamrywiaeth yn well. Ar hyn o bryd mae'r safle'n dir pori ac yn dir âr sydd â gwerth ecolegol isel, ond wrth weithredu Cynllun Seilwaith Gwyrdd bydd y safle'n cael ei wella'n sylweddol ar gyfer bioamrywiaeth drwy greu amrywiaeth o gynefinoedd newydd, gan gynnig bwyd a lloches i fywyd gwyllt.`,
    },
  },
};

const Project = () => {
  const text = useWelshMap(map);
  const { solarpanels, sitemap1, sitemap2 } = useStaticQuery(graphql`
    query {
      solarpanels: file(relativePath: { eq: "Stock3.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      sitemap1: file(relativePath: { eq: "outline3.png" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      sitemap2: file(relativePath: { eq: "plan2.png" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  return (
    <>
      <Helmet>
        <title>Penpergwm Solar Farm | The Proposal</title>
      </Helmet>
      <Content>
        <Img
          className={classes.image}
          fluid={solarpanels.childImageSharp.fluid}
          alt="Some solar panels"
        />
        <h1>{text.title}</h1>
        {text.intro.split("\n").map((words, i) => (
          <p key={i}>{words}</p>
        ))}
      </Content>
      <Content color="grey">
        <h2>{text.location.title}</h2>
        <Img
          className={classes.grainyImage}
          fluid={sitemap1.childImageSharp.fluid}
          alt="OS map showing the border of the site"
        />
        {text.location.content.split("\n").map((words, i) => (
          <p key={i}>{words}</p>
        ))}
        <ul>
          {text.location.bullets.split("\n").map((words, i) => (
            <li key={i}>{words}</li>
          ))}
        </ul>
      </Content>
      <Content>
        <h2>{text.proposal.title}</h2>
        <Img
          className={classes.grainyImage}
          fluid={sitemap2.childImageSharp.fluid}
          alt="OS map showing the border of the site with panel areas and powerlines highlighted"
        />
        {text.proposal.content.split("\n").map((words, i) => (
          <p key={i}>{words}</p>
        ))}
      </Content>
    </>
  );
};

export default Project;
